import { useEffect, useState } from 'react';
import type { RxDocument } from 'rxdb';
import { useRxCollection } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { RxdbCollectionName } from '../rxdbCollectionName';
import type {
  InspectionFiles,
  InspectionFilesCollection,
  InspectionFilesDocument,
} from './inspectionFiles.types';

export const useInspectionFiles = () => {
  const { SiteDiscipline, CurrentUserSelected } = useMst();
  const inspectionFilesCollection: InspectionFilesCollection = useRxCollection(
    RxdbCollectionName.INSPECTION_FILES,
  );

  const [inspectionFilesInitialized, setInspectionFilesInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (inspectionFilesCollection) {
      setInspectionFilesInitialized(true);
    }
  }, [inspectionFilesCollection]);

  const insertIntoInspectionFileRxdb = async (fileArray: InspectionFiles[]) => {
    try {
      await inspectionFilesCollection?.bulkInsert(fileArray);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useInspectionFiles.ts ~ line 31 ~ insertIntoInspectionFileRxdb ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionFiles.tsx ~ line 30 ~ insertIntoInspectionFileRxdb ~',
      );
    }
  };

  /**
   * Subscribe to newly inserted attachments
   *
   * @param {(item: RxChangeEventInsert<InspectionFiles>) => void} next
   * @return {*}
   */
  const subscribeToInspAttachments = (
    handleAttachments: (attachments: InspectionFiles[]) => void,
  ) => {
    if (!inspectionFilesCollection) return;
    const query = inspectionFilesCollection.find();
    query.$.subscribe(event => {
      handleAttachments(event);
    });
  };

  const getInspectionFiles = async (): Promise<InspectionFilesDocument[]> => {
    if (!inspectionFilesCollection) return [];
    let inspectionFiles: InspectionFilesDocument[];
    try {
      const queryObj = {
        selector: {
          $and: [
            { fkAssetInspectionTask: CurrentUserSelected.taskId },
            { fileCategory: 8 },
            { isDelete: 'False' },
          ],
        },
      };

      inspectionFiles = await inspectionFilesCollection?.find(queryObj).exec();
      return inspectionFiles;
    } catch (error) {
      console.log('🚀 ~ file: useInspectionFiles.ts ~ line 67 ~ getInspectionFiles ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionFiles.tsx ~ line 67 ~ getInspectionFiles ~',
      );
      throw new Error(`error ${error}`);
    }
  };

  const updateInspectionFile = async (
    fileUUId: string,
    name: string,
    showOnReport: boolean,
    fileDescription: string,
    isDelete: string,
    fileSortOrder?: number,
    fkInspectorField?: number,
  ): Promise<RxDocument<InspectionFiles> | null> => {
    if (!inspectionFilesCollection) return null;

    try {
      if (inspectionFilesCollection) {
        const updated = await inspectionFilesCollection
          .findOne({
            selector: {
              fileUUID: fileUUId,
            },
          })
          .update({
            $set: {
              name,
              showOnReport,
              fileDescription,
              isDelete,
              fileSortOrder,
              fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
              userModified: '',
              fkInspectorField,
            },
          });
        return updated;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useInspectionFile.ts ~ line 102 ~ updateInspectionFile ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionFile.tsx ~ line 127 ~ updateInspectionFile ~',
      );
    }
    return null;
  };

  const deleteInspectionFile = async (
    fileUUId: string,
    isDelete: string,
    fileSortOrder?: number,
  ): Promise<RxDocument<InspectionFiles> | null> => {
    if (!inspectionFilesCollection) return null;

    try {
      if (inspectionFilesCollection) {
        const updated = await inspectionFilesCollection
          .findOne({
            selector: {
              fileUUID: fileUUId,
            },
          })
          .update({
            $set: {
              isDelete,
              fileSortOrder,
              fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
              userModified: '',
            },
          });
        return updated;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useInspectionFile.ts ~ line 132 ~ deleteInspectionFile ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionFile.tsx ~ line 165 ~ deleteInspectionFile ~',
      );
      throw error;
    }
    return null;
  };

  const getAllInspectionFiles = async (): Promise<InspectionFilesDocument[]> => {
    if (!inspectionFilesCollection) return [];
    let inspectionFiles: InspectionFilesDocument[];
    try {
      const site = SiteDiscipline.siteId;
      const discipline = SiteDiscipline.disciplineId;
      const queryObj = {
        selector: {
          $and: [{ siteId: site }, { disciplineId: discipline }, { isDelete: 'False' }],
        },
      };

      inspectionFiles = await inspectionFilesCollection?.find(queryObj).exec();
      return inspectionFiles;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useInspectionFiles.ts ~ line 160 ~ getAllInspectionFiles ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useInspectionFiles.tsx ~ line 160 ~ getAllInspectionFiles ~',
      );
      throw new Error(`error ${error}`);
    }
  };

  return {
    inspectionFilesInitialized,
    inspectionFilesCollection,
    getInspectionFiles,
    insertIntoInspectionFileRxdb,
    subscribeToInspAttachments,
    updateInspectionFile,
    deleteInspectionFile,
    getAllInspectionFiles,
  };
};

export default useInspectionFiles;
